"use client";

import {
	RiFlagLine,
	RiHeartFill,
	RiStarLine,
	RiUserForbidLine,
	RiUserLine,
} from "@remixicon/react";
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import { Card, CardContent } from "@workspace/ui/components/card";
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuSeparator,
	ContextMenuTrigger,
} from "@workspace/ui/components/context-menu";
import { Spinner } from "@workspace/ui/components/spinner";
import { toast } from "@workspace/ui/hooks/use-sonner-toast";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { memo, useCallback, useState } from "react";
import { ReportContentDialog } from "@/components/features/shared/report-content-dialog";
import { StatusBadge } from "@/components/shared/status-badge";
import { useBlockActions } from "@/hooks/features/social/use-block-actions";
import { cn } from "@/lib/utils";
import { getDisplayAvatarUrl } from "@workspace/shared-utils";
import { renderStarRating } from "@/lib/utils/set-card-helpers";

export type CommunityReview = {
	id: string;
	userId: string;
	rating: number;
	content: string | null;
	loved: boolean;
	sawLive: boolean;
	sawStream: boolean;
	wantToSee: boolean;
	mustSee: boolean;
	createdAt: string;
	updatedAt: string;
	username: string | null;
	fullName: string | null;
	googleAvatarUrl: string | null;
	manualAvatarUrl: string | null;
	avatarSource: string | null;
	isPrivate: boolean;
};

export interface CommunityReviewsListProps {
	reviews: CommunityReview[];
	isLoading: boolean;
	error: unknown;
	isAuthenticated: boolean;
	reportContentType: "set_review" | "show_review";
	emptyEntityLabel: "set" | "show";
	isFetchingNextPage?: boolean;
}

const ReviewItem = memo<{
	review: CommunityReview;
	onReportContent: (review: CommunityReview) => void;
}>(function ReviewItem({ review, onReportContent }) {
	const router = useRouter();
	const [isBlockingUser, setIsBlockingUser] = useState(false);

	const displayAvatar = getDisplayAvatarUrl({
		manualAvatarUrl: review.manualAvatarUrl,
		googleAvatarUrl: review.googleAvatarUrl,
		avatarSource: review.avatarSource as "google" | "manual" | "none" | null | undefined,
	});

	const { blockUser } = useBlockActions();

	const handleBlockUser = useCallback(async () => {
		setIsBlockingUser(true);
		const result = await blockUser(review.userId);
		if (result.success) {
			toast({
				title: "User blocked",
				description: `@${review.username || "User"} has been blocked`,
			});
			router.refresh();
		}
		setIsBlockingUser(false);
	}, [review.userId, review.username, router, blockUser]);

	return (
		<ContextMenu>
			<ContextMenuTrigger asChild>
				<Card
					className="border bg-card shadow-sm cursor-pointer hover:bg-accent/5 active:scale-[0.98] transition-all duration-200"
					onClick={(e) => {
						e.currentTarget.dispatchEvent(
							new MouseEvent("contextmenu", {
								bubbles: true,
								cancelable: true,
								view: window,
								clientX: e.clientX,
								clientY: e.clientY,
							}),
						);
					}}
				>
					<CardContent className="p-3">
						<div className="flex gap-3">
							{/* Left: Profile pic */}
							<div className="flex-shrink-0">
								{review.username ? (
									<Link
										href={`/${review.username}`}
										className="group block rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2"
										aria-label={`View ${review.username} profile`}
									>
										<div className="overflow-hidden rounded-full transition-all duration-200 group-hover:ring-4 group-hover:ring-primary/10 group-active:scale-95 group-active:ring-primary/20">
											<div className="transition-transform duration-200 group-hover:scale-105 group-active:scale-100">
												<Avatar className="h-12 w-12">
													<AvatarImage
														src={displayAvatar || undefined}
														alt={
															review.username || review.fullName || undefined
														}
													/>
													<AvatarFallback className="text-sm">
														{(review.username || review.fullName || "U")
															.charAt(0)
															.toUpperCase()}
													</AvatarFallback>
												</Avatar>
											</div>
										</div>
									</Link>
								) : (
									<Avatar className="h-12 w-12">
										<AvatarImage
											src={displayAvatar || undefined}
											alt={review.fullName || undefined}
										/>
										<AvatarFallback className="text-sm">
											{(review.fullName || "A").charAt(0).toUpperCase()}
										</AvatarFallback>
									</Avatar>
								)}
							</div>

							{/* Middle: Username and Stars column */}
							<div className="flex flex-col justify-start">
								<p className="truncate font-medium text-sm">
									{review.username
										? `@${review.username}`
										: "Anonymous"}
								</p>

								{/* Star rating and heart below username */}
								{(review.rating > 0 || review.loved) && (
									<div className="mt-1 flex items-center gap-1">
										{review.rating > 0 && (
											<div className="flex items-center">
												{renderStarRating({
													rating: review.rating,
													size: "md",
												})}
											</div>
										)}
										{review.loved && (
											<RiHeartFill className="h-5 w-5 text-red-500" />
										)}
									</div>
								)}
							</div>

							{/* Right: Status badges in top right */}
							<div className="ml-auto flex flex-col gap-1">
								{review.mustSee && (
									<StatusBadge status="mustSee" className="justify-center" />
								)}
								{review.wantToSee && !review.mustSee && (
									<StatusBadge status="wantToSee" className="justify-center" />
								)}
								{review.sawLive ? (
									<StatusBadge status="sawLive" className="justify-center" />
								) : review.sawStream ? (
									<StatusBadge status="sawStream" className="justify-center" />
								) : null}
							</div>
						</div>

						{/* Review text content below - full width */}
						{review.content && (
							<div className="mt-3">
								<p className="text-left text-sm">{review.content}</p>
							</div>
						)}
					</CardContent>
				</Card>
			</ContextMenuTrigger>
			<ContextMenuContent className="w-48 rounded-2xl p-1">
				{review.username && (
					<>
						<ContextMenuItem
							onClick={() => router.push(`/${review.username}`)}
							className="gap-2 rounded-t-xl rounded-b-sm cursor-pointer"
						>
							<RiUserLine className="h-4 w-4" />
							View Profile
						</ContextMenuItem>
						<ContextMenuSeparator />
					</>
				)}
				<ContextMenuItem
					onClick={handleBlockUser}
					disabled={isBlockingUser}
					className={cn(
						"gap-2 text-destructive focus:text-destructive cursor-pointer",
						!review.username ? "rounded-t-xl rounded-b-sm" : "rounded-sm",
					)}
				>
					<RiUserForbidLine className="h-4 w-4" />
					{isBlockingUser ? "Blocking..." : "Block User"}
				</ContextMenuItem>
				<ContextMenuItem
					onClick={() => onReportContent(review)}
					className="gap-2 rounded-b-xl rounded-t-sm text-destructive focus:text-destructive cursor-pointer"
				>
					<RiFlagLine className="h-4 w-4" />
					Report Content
				</ContextMenuItem>
			</ContextMenuContent>
		</ContextMenu>
	);
});

export const CommunityReviewsList = memo<CommunityReviewsListProps>(
	function CommunityReviewsList({
		reviews,
		isLoading,
		error,
		isAuthenticated,
		reportContentType,
		emptyEntityLabel,
		isFetchingNextPage,
	}) {
		const [reportDialogOpen, setReportDialogOpen] = useState(false);
		const [selectedReview, setSelectedReview] =
			useState<CommunityReview | null>(null);

		const handleReportContent = useCallback((review: CommunityReview) => {
			setSelectedReview(review);
			setReportDialogOpen(true);
		}, []);

		if (!isAuthenticated) {
			return (
				<Card className="p-8 text-center">
					<h3 className="mb-1 font-semibold text-xl">Sign In to See Reviews</h3>
					<p className="mb-4 text-muted-foreground text-sm">
						Community reviews and ratings are available to signed-in users.
					</p>
					<Button asChild size="sm">
						<Link href="/auth/signin">Sign In</Link>
					</Button>
				</Card>
			);
		}

		if (isLoading) {
			return (
				<div className="space-y-3">
					{[0, 1, 2].map((i) => (
						<Card key={i} className="border bg-card shadow-sm">
							<CardContent className="p-3">
								<div className="flex gap-3">
									<div className="flex-shrink-0">
										<div className="h-12 w-12 animate-pulse rounded-full bg-muted" />
									</div>
									<div className="flex flex-col justify-start">
										<div className="mb-1 h-4 w-20 animate-pulse rounded bg-muted" />
										<div className="h-4 w-16 animate-pulse rounded bg-muted" />
									</div>
									<div className="ml-auto flex flex-col gap-1">
										<div className="h-5 w-16 animate-pulse rounded-lg bg-muted" />
									</div>
								</div>
								<div className="mt-3">
									<div className="h-8 w-full animate-pulse rounded bg-muted" />
								</div>
							</CardContent>
						</Card>
					))}
				</div>
			);
		}

		if (error) {
			return (
				<Card className="p-8 text-center">
					<h3 className="mb-1 font-semibold text-xl">Unable to Load Reviews</h3>
					<p className="mb-4 text-muted-foreground text-sm">
						Something went wrong. Please try refreshing the page.
					</p>
				</Card>
			);
		}

		if (!reviews || reviews.length === 0) {
			return (
				<Card className="p-8 text-center">
					<div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
						<RiStarLine className="h-6 w-6 text-muted-foreground" />
					</div>
					<h3 className="mb-1 font-semibold text-xl">No Reviews Yet</h3>
					<p className="mb-4 text-muted-foreground text-sm">
						Be the first to share your thoughts about this {emptyEntityLabel}.
					</p>
				</Card>
			);
		}

		return (
			<>
				<div className="space-y-3">
					{reviews.map((review) => (
						<ReviewItem
							key={review.id}
							review={review}
							onReportContent={handleReportContent}
						/>
					))}

					{isFetchingNextPage && (
						<div className="flex justify-center py-4">
							<Spinner variant="muted" />
						</div>
					)}
				</div>

				{selectedReview && (
					<ReportContentDialog
						isOpen={reportDialogOpen}
						onClose={() => {
							setReportDialogOpen(false);
							setSelectedReview(null);
						}}
						contentType={reportContentType}
						contentId={selectedReview.id}
						reviewUsername={selectedReview.username}
					/>
				)}
			</>
		);
	},
);
